import { useState, useEffect } from 'react';
import Footer from '../home/components/Footer';
import Navigation from '../home/components/Navigation';
import { blogArticles } from '../../mocks/blogArticles';
import { updateSEO, cleanupStructuredData } from '../../lib/seo';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [displayCount, setDisplayCount] = useState(9);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const categories = ['All', 'Artists', 'Culture', 'Playlists', 'Events'];
  
  const filteredArticles = selectedCategory === 'All' 
    ? blogArticles 
    : blogArticles.filter(article => article.category === selectedCategory);

  const displayedArticles = filteredArticles.slice(0, displayCount);
  const hasMore = displayCount < filteredArticles.length;

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ranchfireradio.com';
    
    updateSEO({
      title: 'RanchFire Live! Podcast - Texas Country Music Stories & Insights | RanchFire Radio',
      description: 'Dive deep into Texas Country, Red Dirt, and Tex-Mex Country music with RanchFire Live! Podcast. Artist interviews, music culture stories, playlists, and event coverage celebrating authentic country music.',
      keywords: 'Texas Country Podcast, Red Dirt Podcast, Country Music Podcast, Texas Music Blog, Artist Interviews, Music Culture, RanchFire Live',
      canonical: '/blog',
      ogTitle: 'RanchFire Live! Podcast - Texas Country Music Stories',
      ogDescription: 'Stories, insights, and deep dives into Texas Country, Southwestern Americana, and Latin Country music',
      ogImage: 'https://public.readdy.ai/ai/img_res/4db4f5f8df3092fb289e8ff3df19c0ba.jpg',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "RanchFire Live! Podcast",
        "description": "Stories, insights, and deep dives into Texas Country, Southwestern Americana, and Latin Country music",
        "url": `${siteUrl}/blog`,
        "publisher": {
          "@type": "Organization",
          "name": "RanchFire Radio",
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/1d2b59dfb9c0bc727c9fa79ffa1643de.png"
          }
        },
        "blogPost": blogArticles.slice(0, 5).map(article => ({
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.image,
          "datePublished": article.date,
          "author": {
            "@type": "Organization",
            "name": "RanchFire Radio"
          }
        }))
      }
    });

    return () => {
      cleanupStructuredData();
    };
  }, []);

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 6);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNewsletterStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formData = new FormData(formElement);
      
      const urlEncodedData = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d609glo3qj6k6vba3mjg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString()
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 bg-gradient-to-br from-amber-900 via-orange-800 to-red-900">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Artistic%20overhead%20view%20of%20vintage%20typewriter%20with%20blank%20paper%20surrounded%20by%20country%20music%20vinyl%20records%20guitar%20picks%20and%20warm%20coffee%20on%20rustic%20wooden%20desk%20with%20warm%20amber%20lighting%20and%20southwestern%20aesthetic%20celebrating%20music%20journalism%20and%20storytelling%20rich%20warm%20tones%20with%20artistic%20composition&width=1920&height=600&seq=ranchfirebloghero&orientation=landscape"
            alt="Podcast Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight mb-4 sm:mb-6 font-display">
            RanchFire live! Podcast
          </h1>
          <p className="text-lg sm:text-xl text-orange-300 max-w-2xl mx-auto leading-relaxed">
            Stories, insights, and deep dives into Texas Country, Southwestern Americana, and Latin Country music
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  setDisplayCount(9);
                }}
                className={`px-4 sm:px-6 py-2 rounded-full font-bold text-xs sm:text-sm uppercase tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {displayedArticles.map((article) => (
              <a
                key={article.id}
                href={`/blog/article?id=${article.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer border border-gray-200"
              >
                <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                  <img 
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                      {article.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <h3 className="text-gray-900 font-black text-lg sm:text-xl mb-2 sm:mb-3 group-hover:text-orange-600 transition-colors leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3 sm:mb-4">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                    <i className="ri-arrow-right-line text-orange-600 text-base sm:text-lg group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center mt-12 sm:mt-16">
              <button 
                onClick={handleLoadMore}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-orange-600/30 whitespace-nowrap cursor-pointer"
              >
                Load More Episodes
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-orange-600 to-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <i className="ri-mail-line text-5xl sm:text-6xl text-white mb-4 sm:mb-6"></i>
          <h2 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight mb-3 sm:mb-4 font-display">
            Join the Fans Newsletter
          </h2>
          <p className="text-white/90 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
            Get the latest episodes, playlists, and Texas Country news delivered straight to your inbox every week
          </p>
          
          {newsletterStatus === 'success' && (
            <div className="mb-6 p-4 bg-white/20 border border-white/40 text-white rounded-lg max-w-xl mx-auto">
              <div className="flex items-center justify-center gap-2">
                <i className="ri-checkbox-circle-fill text-xl"></i>
                <span className="font-bold">Successfully subscribed!</span>
              </div>
            </div>
          )}

          {newsletterStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg max-w-xl mx-auto">
              <div className="flex items-center justify-center gap-2">
                <i className="ri-error-warning-fill text-xl"></i>
                <span className="font-bold">Something went wrong. Please try again.</span>
              </div>
            </div>
          )}

          <form onSubmit={handleNewsletterSubmit} data-readdy-form id="blog-newsletter-form" className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-xl mx-auto">
            <div className="w-full sm:flex-1">
              <label htmlFor="blog-email" className="block text-white font-bold text-sm mb-2 text-left">
                Email Address
              </label>
              <input 
                type="email"
                id="blog-email"
                name="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-6 py-3 sm:py-4 rounded-full text-gray-900 text-sm focus:outline-none focus:ring-4 focus:ring-white/30"
              />
            </div>
            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-gray-900 hover:bg-black text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed sm:mt-7"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
